import React, { useEffect, useState } from "react";
import { Quote, X } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { TESTIMONIALS } from "@/lib/data";

const Testimonials = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const open = openIdx !== null ? TESTIMONIALS[openIdx] : null;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpenIdx(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const card = (t, i, ariaHidden) => (
    <button
      key={`${t.author}-${i}`}
      onClick={() => setOpenIdx(i % TESTIMONIALS.length)}
      aria-hidden={ariaHidden || undefined}
      tabIndex={ariaHidden ? -1 : 0}
      data-testid={`review-card-${i % TESTIMONIALS.length}`}
      className="card-lift glass rounded-3xl p-7 w-[320px] sm:w-[400px] shrink-0 mr-6 text-left flex flex-col cursor-pointer"
    >
      <Quote size={22} className="text-[#D4AF37]/60" strokeWidth={1.2} />
      <blockquote className="font-display italic text-lg sm:text-xl text-[#FDFBF7] leading-snug mt-4 quote-clamp flex-1">
        "{t.quote}"
      </blockquote>
      <span className="mt-5 pt-4 border-t border-white/10 flex items-end justify-between gap-3">
        <span>
          <span className="block text-[#F3D068] font-medium">{t.author}</span>
          <span className="block text-xs text-[#8A99B5] mt-1">{t.role}</span>
        </span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#F3D068]/90 whitespace-nowrap">
          Read full →
        </span>
      </span>
    </button>
  );

  return (
    <section className="relative py-16 sm:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Word Spreads"
          title={
            <>
              What people say after <em className="text-gold-gradient italic">the light finds them</em>
            </>
          }
          sub="Reviews scroll by — tap any card to read the full review."
        />
      </div>

      <Reveal delay={0.1}>
        <div className="relative mt-14" data-testid="reviews-marquee">
          <div className="flex w-max animate-marquee marquee-paused pl-6">
            {TESTIMONIALS.map((t, i) => card(t, i, false))}
            {TESTIMONIALS.map((t, i) => card(t, i + TESTIMONIALS.length, true))}
          </div>
          <div className="absolute inset-y-0 left-0 w-16 sm:w-32 bg-gradient-to-r from-[#0B1021] to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-16 sm:w-32 bg-gradient-to-l from-[#0B1021] to-transparent pointer-events-none" />
        </div>
      </Reveal>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-5 bg-black/80 backdrop-blur-sm"
          onClick={() => setOpenIdx(null)}
          data-testid="review-modal"
        >
          <div
            className="glass rounded-3xl p-8 sm:p-10 max-w-lg w-full border-[#F3D068]/30 shadow-[0_30px_90px_rgba(0,0,0,0.8)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <Quote size={28} className="text-[#D4AF37]/60" strokeWidth={1.2} />
              <button
                onClick={() => setOpenIdx(null)}
                data-testid="review-modal-close"
                aria-label="Close review"
                className="text-[#8A99B5] hover:text-[#F3D068] transition-colors p-1"
              >
                <X size={20} />
              </button>
            </div>
            <blockquote className="font-display italic text-xl sm:text-2xl text-[#FDFBF7] leading-relaxed mt-5">
              "{open.quote}"
            </blockquote>
            <div className="mt-7 pt-5 border-t border-white/10">
              <p className="text-[#F3D068] font-medium">{open.author}</p>
              <p className="text-xs text-[#8A99B5] mt-1">{open.role}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Testimonials;
